import { InviteSummary } from "@/pages/invites/invite.utils";

export enum InviteStatusType {
  Expired = "expired",
  AlreadyUsed = "already-used",
  NotFound = "not-found",
  Error = "error",
}

export interface InviteStatusCopyOverrides {
  title?: string | null;
  body?: string | null;
}

const STATUS_COPY: Record<InviteStatusType, { title: () => string; body: string | null }> = {
  [InviteStatusType.Expired]: {
    title: () => "This invitation has expired.",
    body: "Ask the sender for a new invitation.",
  },
  [InviteStatusType.AlreadyUsed]: {
    title: () => "This invitation has been used.",
    body: "Ask the sender for a new invitation.",
  },
  [InviteStatusType.NotFound]: {
    title: () => "We couldn't open this invitation.",
    body: null,
  },
  [InviteStatusType.Error]: {
    title: () => "We couldn't process this invitation.",
    body: "Please try again.",
  },
};

export const getNormalizedInviteToken = ({ token }: { token: string | null }): string =>
  token?.trim() ?? "";

export const getInviteSharedItemNoun = ({
  sharedItemKind,
}: {
  sharedItemKind?: InviteSummary["sharedItemKind"];
}): string => {
  switch (sharedItemKind) {
    case "COLLECTION":
      return "collection";
    case "NOTE":
    default:
      return "note";
  }
};

export const getInviteSharedItemTitle = ({ invite }: { invite?: InviteSummary | null }): string => {
  const noun = getInviteSharedItemNoun({ sharedItemKind: invite?.sharedItemKind });
  const title = invite?.displayTitle?.trim();

  if (title) {
    return title;
  }

  return `this ${noun}`;
};

export const getInviteInviterLabel = ({ invite }: { invite: InviteSummary }): string => {
  const displayName = invite.inviterDisplayName?.trim();
  if (displayName) {
    return displayName;
  }

  const email = invite.inviterEmail?.trim();
  if (email) {
    return email;
  }

  return "a teammate";
};

export const getInviteSharedItemKindLabel = ({
  sharedItemKind,
}: {
  sharedItemKind: InviteSummary["sharedItemKind"];
}): string => {
  const noun = getInviteSharedItemNoun({ sharedItemKind });
  return noun.charAt(0).toUpperCase() + noun.slice(1);
};

export const getInviteDescription = ({ invite }: { invite: InviteSummary }): string => {
  const inviterLabel = getInviteInviterLabel({ invite });
  const inviteTitle = getInviteSharedItemTitle({ invite });

  return `${inviterLabel} has invited you to collaborate on ${inviteTitle}. Accept the invite below to start collaborating.`;
};

export const getInviteStatusCopy = ({
  type,
  overrides,
}: {
  type: InviteStatusType;
  overrides?: InviteStatusCopyOverrides;
}): { title: string; body: string | null } => {
  const defaults = STATUS_COPY[type];

  return {
    title: overrides?.title ?? defaults.title(),
    body: overrides?.body ?? defaults.body,
  };
};

export const getInviteAcceptedMessage = ({
  invite,
  overrideMessage,
}: {
  invite: InviteSummary | null;
  overrideMessage?: string | null;
}): string => {
  if (overrideMessage?.trim()) {
    return overrideMessage.trim();
  }

  return `Invitation accepted! You now have access to ${getInviteSharedItemTitle({ invite })}.`;
};

export const getInviteViewerGreeting = ({ viewerName }: { viewerName: string | null }): string => {
  if (viewerName?.trim()) {
    return `Welcome to Mem, ${viewerName.trim()}`;
  }

  return "Welcome to Mem";
};
